// Super-admin dashboard: basic billing numbers (from GET /company/billing-summary).
// Dollar amounts are estimates from plan prices; the database has no payment ledger yet.

const money = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 2 });
const count = (n) => Number(n || 0).toLocaleString('en-US');
const companies = (n) => `${count(n)} ${Number(n) === 1 ? 'company' : 'companies'}`;
const shortDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

const card = {
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e8ecf4',
  boxShadow: '0 1px 3px rgba(15,23,42,.04)',
};

const Stat = ({ label, value, note, color = '#0f172a' }) => (
  <div style={{ ...card, padding: '18px 20px' }}>
    <div style={{ fontSize: 13, fontWeight: 500, color: '#64748b', marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 24, fontWeight: 600, color, lineHeight: 1.15, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
    {note ? <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{note}</div> : null}
  </div>
);

const th = { padding: '10px 16px', fontSize: 12, fontWeight: 600, color: '#64748b', textAlign: 'left', borderBottom: '1px solid #edf0f7', background: '#f8f9fc', whiteSpace: 'nowrap' };
const td = { padding: '11px 16px', fontSize: 14, color: '#334155', borderBottom: '1px solid #f0f2f8' };
const num = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

const statusRows = [
  { key: 'paying', label: 'Paid plan (active)', color: '#15803d' },
  { key: 'trial', label: 'On free trial', color: '#1d4ed8' },
  { key: 'paymentFailed', label: 'Payment failed', color: '#b91c1c' },
  { key: 'trialExpired', label: 'Trial expired, no card', color: '#b45309' },
  { key: 'canceled', label: 'Canceled', color: '#64748b' },
  { key: 'noPlan', label: 'No plan chosen', color: '#64748b' },
  { key: 'demo', label: 'Demo mode (exploring, no plan yet)', color: '#b45309' },
  { key: 'free', label: 'Free account (set by BidBuyZoom)', color: '#6d28d9' },
  { key: 'other', label: 'Other / incomplete', color: '#64748b' },
];

const BillingOverview = ({ data }) => {
  if (!data) return null;
  const c = data.counts || {};
  const plans = data.plans || [];
  const attention = data.attention || [];

  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ fontSize: 16, fontWeight: 600, color: '#0f172a', margin: '0 0 4px' }}>Billing</h3>
      <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 14px' }}>
        Dollar amounts are estimates from plan prices, not payments received.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 16 }}>
        <Stat label="Paying companies" value={count(c.paying)} note={`${count(c.trial)} on free trial`} color="#15803d" />
        <Stat label="Monthly recurring (est.)" value={money(data.monthlyRecurring)} note="Active plans, yearly ÷ 12" />
        <Stat label="Billed this month (est.)" value={money(data.billedThisMonth?.amount)} note={`${companies(data.billedThisMonth?.count)} charged`} />
        <Stat label="Renewals next 30 days" value={money(data.renewalsNext30Days?.amount)} note={companies(data.renewalsNext30Days?.count)} />
        <Stat label="Payment failed" value={count(c.paymentFailed)} note="Card declined or past due" color={c.paymentFailed ? '#b91c1c' : '#0f172a'} />
        <Stat label="Active without a paid plan" value={count(data.activeWithoutPaidPlan)} note="Set active by hand or never paid" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16 }}>
        <div style={{ ...card, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Companies by billing status</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {statusRows.filter((r) => r.key !== 'other' || c.other).map((r) => (
                <tr key={r.key}>
                  <td style={td}>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 8, background: r.color, marginRight: 8 }} />
                    {r.label}
                  </td>
                  <td style={num}>{count(c[r.key])}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={{ ...card, overflow: 'hidden' }}>
          <div style={{ padding: '14px 16px', fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Needs attention</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {attention.length ? attention.map((a) => (
                <tr key={`${a.id}-${a.issue}`}>
                  <td style={td}>
                    <div style={{ fontWeight: 500, color: '#0f172a' }}>{a.name}</div>
                    <div style={{ fontSize: 12, color: '#94a3b8' }}>{a.plan}</div>
                  </td>
                  <td style={{ ...td, color: a.issue === 'Payment failed' ? '#b91c1c' : '#b45309' }}>{a.issue}</td>
                  <td style={{ ...num, color: '#64748b', whiteSpace: 'nowrap' }}>{shortDate(a.date)}</td>
                </tr>
              )) : (
                <tr><td style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 28 }}>Nothing right now</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

        <div style={{ ...card, overflow: 'hidden', marginTop: 16 }}>
          <div style={{ padding: '14px 16px', fontSize: 14, fontWeight: 600, color: '#0f172a' }}>Plans</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Plan</th>
                  <th style={{ ...th, textAlign: 'right' }}>Price</th>
                  <th style={{ ...th, textAlign: 'right' }}>Paying</th>
                  <th style={{ ...th, textAlign: 'right' }}>Trial</th>
                  <th style={{ ...th, textAlign: 'right' }}>Monthly (est.)</th>
                </tr>
              </thead>
              <tbody>
                {plans.length ? plans.map((p) => (
                  <tr key={`${p.planName}-${p.period}`}>
                    <td style={td}>
                      <div>{p.planName}</div>
                      {p.period ? <div style={{ fontSize: 12, color: '#94a3b8' }}>{p.period}</div> : null}
                    </td>
                    <td style={num}>{money(p.price)}</td>
                    <td style={num}>{count(p.paying)}</td>
                    <td style={num}>{count(p.trial)}</td>
                    <td style={num}>{money(p.monthlyValue)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: 28 }}>No companies on a plan yet</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
    </div>
  );
};

export default BillingOverview;
